import { useState, useEffect, useRef } from "react";
import { Card, Button, Modal } from "react-bootstrap";
import moment from "moment";
import { instanceMob } from "../../../utils/utils";
import { getBackendError } from "../../../utils/error-helpers";

const CardBiometrics = ({ device, locations = [], onUpdate, onShowAlert }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(device.location?.id || "");
    const [isSaving, setIsSaving] = useState(false);
    const [showLocMenu, setShowLocMenu] = useState(false);
    const [showSnapshotModal, setShowSnapshotModal] = useState(false);
    const [snapshotUrl, setSnapshotUrl] = useState(null);
    const [isCapturingSnapshot, setIsCapturingSnapshot] = useState(false);
    const [isOpeningGate, setIsOpeningGate] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [logs, setLogs] = useState([]);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);

    const actionsRef = useRef(null);
    const locRef = useRef(null);

    // Outside click handling for location dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (locRef.current && !locRef.current.contains(event.target)) {
                setShowLocMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Cleanup snapshot URL
    useEffect(() => {
        return () => {
            if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
        };
    }, [snapshotUrl]);

    // Determine icon based on device type
    const getDeviceIcon = () => {
        switch (device.type) {
            case 10: // Face
                return "bi-person-bounding-box";
            case 20: // Fingerprint
                return "bi-fingerprint";
            default:
                return "bi-fingerprint";
        }
    };

    // Determine if device is online (status 10 = Online)
    const isOnline = device.current_status === 10;
    const isEnabled = device.enable;
    const statusClass = isOnline && isEnabled ? 'online' : 'offline';

    const parseBlobError = async (err) => {
        if (err.response?.data instanceof Blob) {
            try {
                const text = await err.response.data.text();
                const json = JSON.parse(text);
                return getBackendError({ ...err, response: { ...err.response, data: json } });
            } catch (e) {
                return "Error al procesar la respuesta del servidor";
            }
        }
        return getBackendError(err);
    };

    const handleSync = () => {
        instanceMob.post(`/biometrics/location/sync`, { location_id: device.location?.id, type: device.type })
            .then((res) => {
                const message = res.data?.message || "Sincronización iniciada";
                if (onShowAlert) onShowAlert("Éxito", message, "success");
            })
            .catch(err => {
                console.error(err);
                if (onShowAlert) onShowAlert("Error de Sincronización", getBackendError(err), "error");
            });
    };

    const handleSnapshot = () => {
        setIsCapturingSnapshot(true);
        instanceMob.post(`/biometrics/snapshot`, { biometric_id: device.id }, { responseType: 'blob' })
            .then((res) => {
                if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
                const url = URL.createObjectURL(res.data);
                setSnapshotUrl(url);
                setShowSnapshotModal(true);
                setIsCapturingSnapshot(false);
            })
            .catch(async (err) => {
                console.error(err);
                setIsCapturingSnapshot(false);
                const errorMsg = await parseBlobError(err);
                if (onShowAlert) onShowAlert("Error de Captura", errorMsg, "error");
            });
    };

    const handleRemoteOpen = () => {
        setIsOpeningGate(true);
        instanceMob.post(`/biometrics/${device.id}/open-gate`)
            .then(() => {
                if (onShowAlert) onShowAlert("Éxito", "Apertura remota iniciada", "success");
            })
            .catch(err => {
                console.error(err);
                if (onShowAlert) onShowAlert("Error de Apertura Remota", getBackendError(err), "error");
            })
            .finally(() => {
                setIsOpeningGate(false);
            });
    };

    const fetchLogs = async () => {
        setIsLoadingLogs(true);
        try {
            const res = await instanceMob.get(`/biometrics/customer/logs?biometric=${device.id}`);
            // Handle paginated response structure
            const logsData = res.data?.results || res.data || [];
            const mappedData = (Array.isArray(logsData) ? logsData : []).map(x => ({
                id: x.id,
                customer_id: x.customer_id,
                customer_name: x.customer_name || "N/A",
                biometric_id: x.biometric_id,
                location: x.location,
                is_allowed: x.is_allowed,
                date: x.access_date ? moment(x.access_date, "DD/MM/YYYY hh:mm:ss A").format("DD/MM/YYYY HH:mm") : "N/A",
                status: x.is_allowed ? "Permitido" : "Denegado"
            }));
            setLogs(mappedData);
        } catch (err) {
            console.error("Error fetching logs:", err);
            setLogs([]);
            if (onShowAlert) onShowAlert("Error", getBackendError(err), "error");
        } finally {
            setIsLoadingLogs(false);
        }
    };

    const handleViewLogs = () => {
        setShowLogsModal(true);
        fetchLogs();
    };

    const saveChanges = () => {
        setIsSaving(true);
        instanceMob.patch(`/biometrics/${device.id}/`, { location_id: selectedLocation, is_active: device.enable })
            .then(() => {
                setIsEditing(false);
                setIsSaving(false);
                if (onUpdate) onUpdate();
                if (onShowAlert) onShowAlert("Éxito", "Ubicación actualizada correctamente", "success");
            })
            .catch(err => {
                console.error(err);
                setIsSaving(false);
                if (onShowAlert) onShowAlert("Error", getBackendError(err), "error");
            });
    };

    return (
        <Card className="w-100 rounded-4 h-100 biometric-card border-1">
            <Card.Body className="d-flex flex-column p-4 h-100">
                {/* Header: Icon, Name, and Primary Actions */}
                <div className="biometric-card-header">
                    <div className={`biometric-icon ${statusClass}`}>
                        <i className={getDeviceIcon()}></i>
                    </div>

                    <div className="flex-grow-1 overflow-hidden">
                        <h6 className="biometric-device-name text-truncate">
                            {device.name || "Dispositivo biométrico"}
                        </h6>
                    </div>

                    <div className="biometric-actions-container d-flex gap-2">
                        <button
                            className="biometric-action-btn"
                            title="Snapshot"
                            onClick={handleSnapshot}
                            disabled={isCapturingSnapshot}
                        >
                            {isCapturingSnapshot ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <i className="bi bi-camera"></i>
                            )}
                        </button>
                        <button
                            className="biometric-action-btn"
                            title="Apertura Remota"
                            onClick={handleRemoteOpen}
                            disabled={isOpeningGate}
                        >
                            {isOpeningGate ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <i className="bi bi-unlock"></i>
                            )}
                        </button>
                        <button
                            className="biometric-action-btn"
                            title="Editar"
                            onClick={() => setIsEditing(!isEditing)}
                        >
                            <i className="bi bi-pencil"></i>
                        </button>
                    </div>
                </div>

                {/* Body: Info or Edit Mode */}
                <div className="flex-grow-1">
                    {isEditing ? (
                        <div className="edit-mode-container">
                            <div className="biometric-dropdown-container w-100" ref={locRef}>
                                <button
                                    className="btn btn-light dropdown-toggle custom-dropdown d-flex flex-row justify-content-between align-items-center w-100"
                                    onClick={() => setShowLocMenu(!showLocMenu)}
                                >
                                    <span className="dropdown-span">
                                        {locations.find(l => l.id === selectedLocation)?.name || "Seleccionar locación..."}
                                    </span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none">
                                        <path d="M7.54236 9.08938C7.14647 9.08938 6.75052 8.93837 6.4484 8.63626L0.45311 2.64096C-0.151037 2.03681 -0.151037 1.05726 0.45311 0.453101C1.05723 -0.151034 2.03682 -0.151034 2.64095 0.453101L7.54236 5.35457L12.4438 0.453101C13.0479 -0.151034 14.0275 -0.151034 14.6316 0.453101C15.2358 1.05726 15.2358 2.03681 14.6316 2.64096L8.63633 8.63626C8.33423 8.93837 7.93825 9.08938 7.54236 9.08938Z" fill="#9DACBD" />
                                    </svg>
                                </button>

                                {showLocMenu && (
                                    <div className="biometric-dropdown-menu w-100" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {locations.map(loc => (
                                            <button
                                                key={loc.id}
                                                className="biometric-dropdown-item"
                                                onClick={() => { setSelectedLocation(loc.id); setShowLocMenu(false); }}
                                            >
                                                {loc.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                <Button
                                    size="sm"
                                    className="biometric-cancel-btn w-100"
                                    onClick={() => setIsEditing(false)}
                                    disabled={isSaving}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="sm"
                                    className="biometric-save-btn w-100"
                                    onClick={saveChanges}
                                    disabled={isSaving}
                                >
                                    {isSaving ? "..." : "Guardar"}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-2 py-2">
                            {/* Location */}
                            <div className="biometric-info-row">
                                <i className="bi bi-geo-alt biometric-info-icon"></i>
                                <span className="biometric-info-text text-truncate">
                                    {device.location?.name || "Sin locación asignada"}
                                </span>
                            </div>

                            {/* Serial number */}
                            <div className="biometric-info-row">
                                <i className="bi bi-hash biometric-info-icon"></i>
                                <span className="biometric-serial">
                                    {device.serial || device.id || "N/A"}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer: Timestamp, Status and Secondary Actions */}
                {!isEditing && (
                    <div className="biometric-card-footer">
                        <div className="biometric-timestamp">
                            <i className="bi bi-clock-history"></i>
                            <div className="d-flex flex-column">
                                <span className="timestamp-label">Último reporte</span>
                                <span className="timestamp-value">
                                    {(device.last_report_date || device.updated_at) ? new Date(device.last_report_date || device.updated_at).toLocaleString('es-ES', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    }) : 'Sin registro'}
                                </span>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="biometric-action-btn"
                                title="Ver Logs"
                                onClick={handleViewLogs}
                            >
                                <i className="bi bi-file-text"></i>
                            </button>
                            <button
                                className="biometric-action-btn"
                                title="Sincronizar"
                                onClick={handleSync}
                            >
                                <i className="bi bi-arrow-repeat"></i>
                            </button>
                            <div className={`status-dot ${statusClass}`}></div>
                        </div>
                    </div>
                )}
                {/* Modal for Snapshot Preview */}
                <Modal
                    show={showSnapshotModal}
                    onHide={() => setShowSnapshotModal(false)}
                    centered
                    size="lg"
                    className="biometric-snapshot-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fs-6 fw-bold">Captura de Dispositivo</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4 d-flex justify-content-center align-items-center">
                        {snapshotUrl ? (
                            <div className="snapshot-preview-container">
                                <img
                                    src={snapshotUrl}
                                    alt="Device Snapshot"
                                    className="img-fluid rounded-3 shadow-sm border"
                                />
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary mb-3" role="status"></div>
                                <p className="text-muted mb-0">No se pudo cargar la imagen.</p>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>

                {/* Modal for Logs */}
                <Modal
                    show={showLogsModal}
                    onHide={() => setShowLogsModal(false)}
                    centered
                    size="lg"
                    className="biometric-logs-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fs-6 fw-bold">
                            Logs de Acceso - {device.name || "Dispositivo"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {isLoadingLogs ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary mb-3" role="status"></div>
                                <p className="text-muted mb-0">Cargando logs...</p>
                            </div>
                        ) : logs.length > 0 ? (
                            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <table className="table table-hover">
                                    <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Usuario</th>
                                            <th>Ubicación</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map(log => (
                                            <tr key={log.id}>
                                                <td className="small">{log.date}</td>
                                                <td className="small">{log.customer_name}</td>
                                                <td className="small">{log.location || "N/A"}</td>
                                                <td>
                                                    <span className={`badge ${log.is_allowed ? 'bg-success' : 'bg-danger'}`}>
                                                        {log.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                                <p className="text-muted mb-0">No hay logs disponibles para este dispositivo.</p>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>
            </Card.Body>
        </Card>
    );
};

export default CardBiometrics;
